import defaultEmailsTheme from "@babackapp/blocks-editor/constants/default-emails-theme";
import { EditorContainerWidth } from "@babackapp/blocks-editor/constants/editor-constants";
import {
  EditorHistoryProvider,
  useEditorHistoryContext,
} from "@babackapp/blocks-editor/contexts/editor-history-context";
import {
  EditorSelectedBlockProvider,
  useEditorSelectedBlock,
} from "@babackapp/blocks-editor/contexts/editor-selected-block-context";
import EmailEditor, { type EditorMode } from "@babackapp/blocks-editor/editor";
import { useEditorKeyboardShortcuts } from "@babackapp/blocks-editor/hooks/use-editor-keyboard-shortcuts";
import WebEditor from "@babackapp/blocks-editor/web-editor";
import { useRouter } from "@tanstack/react-router";
import type { InitialOverrides } from "./context/templates-editor-templates-context";
import { TrackingTemplatesRoutingProvider } from "./tracking-routing-provider";
import { useTemplatesEditorConfig } from "./context/templates-editor-config-context";
import { EmailsThemeDto } from "@babackapp/private-api-client/generated/schema";
import { ContextualTipsProvider } from "@babackapp/ui/components/contextual-tips";
import { cn } from "@babackapp/ui/lib/utils";
import {
  useVariablesDictionary,
  useUnavailableVariableIds,
  VariablesDictionaryProvider,
} from "@babackapp/variables-registry";
import { Button } from "@babackapp/ui/components/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@babackapp/ui/components/dialog";
import { FloatingBar, FloatingBarGroup } from "@babackapp/ui/components/floating-panel";
import { useTranslation } from "react-i18next";
import { Save } from "lucide-react";
import { useEffect, useState } from "react";
import { FormProvider, useForm, useFormContext } from "react-hook-form";
import { BottomBar } from "./components/bottom-bar";
import { ValidationErrorsDisplay } from "./components/validation-errors-display";
import useTemplateValidation from "./hooks/use-template-validation";
import { EditorDndContext } from "./components/dnd-context";
import { LeftSidebar } from "./components/left-sidebar/left-sidebar";
import { RightSidebar } from "./components/right-sidebar";
import TopBar from "./components/top-bar";
import {
  TemplatesEditorLocaleProvider,
  useEmailEditorLocale,
} from "./context/templates-editor-locale-context";
import { TemplatesEditorSettingsProvider } from "./context/templates-editor-settings-context";
import { TemplatesEditorTemplatesProvider } from "./context/templates-editor-templates-context";
import {
  TemplatesEditorViewportProvider,
  useTemplatesEditorViewport,
} from "./context/templates-editor-viewport-context";
import useCurrentShopSuspense, {
  useCurrentPublicShopSuspense,
} from "./hooks/use-current-shop-suspense";
import { useUnsavedChanges } from "./hooks/use-unsaved-changes";
import { EmailTemplateFormData } from "./types";

export type TemplateEditorProps = {
  currentTemplateId: string;
  devMode?: boolean;
  localEmailsTestingUrl?: string;
  openThemeSettings?: () => void;
  currentUserEmail?: string;
  isWorkflowCustomMode?: boolean;
  initialOverrides?: InitialOverrides;
  hiddenBlocks?: string[];
  onToggleBlockVisibility?: (blockId: string) => void;
  customizedBlockIds?: string[];
  onFormChange?: (data: EmailTemplateFormData) => void;
  onWorkflowSave?: () => void;
  onWorkflowCancel?: () => void;
  workflowSubject?: Record<string, string>;
};

type EditorCoreProps = TemplateEditorProps & {
  /** Reports the form's dirty state up so a wrapper can apply router blocking. */
  onDirtyChange?: (isDirty: boolean) => void;
};

/**
 * Router-agnostic editor core. Assumes a TemplatesEditorRoutingProvider and a
 * TemplatesEditorConfigProvider above it (supplying shopId + navigation and the
 * domain variables/groups). Shared by the tracking wrapper (TemplatesEditor)
 * and the return wrapper (ReturnTemplatesEditor).
 */
// craftsman-ignore: TS002
export function EditorCore(props: EditorCoreProps) {
  const { variables: defaultEditorVariables } = useTemplatesEditorConfig();
  const outerUnavailableVariableIds = useUnavailableVariableIds();
  const { data: shop } = useCurrentShopSuspense();
  const form = useForm<EmailTemplateFormData>({
    defaultValues: {
      subject: {},
      preview: {},
      content: {
        beforeBlocks: [],
        afterBlocks: [],
        insideBlocks: [],
      },
      assets: { uploads: {} },
      title: "",
      theme:
        (shop.trackingPreferences?.emailsTheme as Required<EmailsThemeDto>) ||
        defaultEmailsTheme,
    },
  });

  useEffect(() => {
    const newTheme = (shop.trackingPreferences?.emailsTheme ||
      defaultEmailsTheme) as Required<EmailsThemeDto>;
    form.setValue("theme", newTheme, { shouldDirty: false });
  }, [form, shop.trackingPreferences?.emailsTheme]);

  return (
    <FormProvider {...form}>
      <EditorHistoryProvider options={{ maxHistorySize: 50 }}>
        <TemplatesEditorTemplatesProvider
          currentTemplateId={props.currentTemplateId}
          initialOverrides={props.initialOverrides}
        >
          <TemplatesEditorSettingsProvider
            localEmailsTestingUrl={
              props.localEmailsTestingUrl || "http://localhost:3001"
            }
            devMode={!!props.devMode}
            openThemeSettings={props.openThemeSettings}
            currentUserEmail={props.currentUserEmail}
          >
            <TemplatesEditorLocaleProvider>
              <TemplatesEditorViewportProvider>
                <VariablesDictionaryProvider
                  variablesDefinition={defaultEditorVariables}
                  unavailableVariableIds={outerUnavailableVariableIds}
                >
                  <ContextualTipsProvider>
                    <Content
                      isWorkflowCustomMode={props.isWorkflowCustomMode}
                      hiddenBlocks={props.hiddenBlocks}
                      onToggleBlockVisibility={props.onToggleBlockVisibility}
                      customizedBlockIds={props.customizedBlockIds}
                      onFormChange={props.onFormChange}
                      onWorkflowSave={props.onWorkflowSave}
                      onWorkflowCancel={props.onWorkflowCancel}
                      workflowSubject={props.workflowSubject}
                      onDirtyChange={props.onDirtyChange}
                    />
                  </ContextualTipsProvider>
                </VariablesDictionaryProvider>
              </TemplatesEditorViewportProvider>
            </TemplatesEditorLocaleProvider>
          </TemplatesEditorSettingsProvider>
        </TemplatesEditorTemplatesProvider>
      </EditorHistoryProvider>
    </FormProvider>
  );
}

/**
 * Tracking templates editor — used by track-office and the workflows editor.
 *
 * Self-wires routing from TanStack Router (the host router in those apps) and
 * blocks internal navigation while the form is dirty. Public signature is
 * unchanged from before the core extraction.
 */
export default function TemplatesEditor(props: TemplateEditorProps) {
  const router = useRouter();
  const [isDirty, setIsDirty] = useState(false);

  // Block internal SPA navigation while there are unsaved changes.
  useEffect(() => {
    if (!isDirty) return;
    const unsubscribe = router.subscribe("onBeforeLoad", (event) => {
      const shouldLeave = window.confirm(
        "Vous avez des modifications non enregistrées dans l'éditeur.",
      );
      if (!shouldLeave) {
        if (
          "preventDefault" in event &&
          typeof event.preventDefault === "function"
        ) {
          event.preventDefault();
        }
        throw new Error("Navigation cancelled by user");
      }
    });
    return unsubscribe;
  }, [isDirty, router]);

  return (
    <TrackingTemplatesRoutingProvider>
      <EditorCore {...props} onDirtyChange={setIsDirty} />
    </TrackingTemplatesRoutingProvider>
  );
}

function Content({
  isWorkflowCustomMode,
  hiddenBlocks,
  onToggleBlockVisibility,
  customizedBlockIds,
  onFormChange,
  onWorkflowSave,
  onWorkflowCancel,
  workflowSubject,
  onDirtyChange,
}: {
  isWorkflowCustomMode?: boolean;
  hiddenBlocks?: string[];
  onToggleBlockVisibility?: (blockId: string) => void;
  customizedBlockIds?: string[];
  onFormChange?: (data: EmailTemplateFormData) => void;
  onWorkflowSave?: () => void;
  onWorkflowCancel?: () => void;
  workflowSubject?: Record<string, string>;
  onDirtyChange?: (isDirty: boolean) => void;
}) {
  const { data: shop } = useCurrentPublicShopSuspense();
  const form = useFormContext<EmailTemplateFormData>();
  const isDirty = form.formState.isDirty;
  const { variables } = useVariablesDictionary();
  const templateType = form.watch("type") || "email";
  const editorMode: EditorMode = isWorkflowCustomMode
    ? "workflowCustom"
    : "default";

  const shouldGuardUnsavedChanges = isDirty && !isWorkflowCustomMode;
  useUnsavedChanges(shouldGuardUnsavedChanges);

  // Report dirty state up so the host wrapper can block internal navigation.
  useEffect(() => {
    onDirtyChange?.(shouldGuardUnsavedChanges);
  }, [shouldGuardUnsavedChanges, onDirtyChange]);

  // Notify parent of form changes in workflow mode
  useEffect(() => {
    if (!onFormChange) return;
    const subscription = form.watch((data) => {
      onFormChange(data as EmailTemplateFormData);
    });
    return () => subscription.unsubscribe();
  }, [form, onFormChange]);

  const { currentLocale, setCurrentLocale, localeCodes, defaultLocale } =
    useEmailEditorLocale();
  const { viewport } = useTemplatesEditorViewport();
  const { contentMaxWidth } = useTemplatesEditorConfig();

  // Setup undo/redo keyboard shortcuts
  const { undo, redo, canUndo, canRedo } = useEditorHistoryContext();
  useEditorKeyboardShortcuts({
    onUndo: undo,
    onRedo: redo,
    canUndo,
    canRedo,
  });

  const editorProps = {
    shop,
    currentLocale,
    onLocaleChange: setCurrentLocale,
    viewport,
    variables,
    contentMaxWidth,
    editorMode,
    hiddenBlocks,
    onToggleBlockVisibility,
    customizedBlockIds,
    localeCodes,
    defaultLocale,
  };

  const editorContent = (
    <div className="relative flex h-full w-full bg-editor-background bg-[radial-gradient(var(--border)_1px,transparent_1px)] [background-size:16px_16px]">
      {!isWorkflowCustomMode && <LeftSidebar />}
      <EditorSelectedBlockProvider>
        <BlockUnselectHandler />
        <div className="flex justify-between h-full flex-1 relative items-center flex-col w-full pb-4 pointer-events-none">
          {/* pointer-events-none to make editor bg clickable */}
          <div className="z-[50] pointer-events-auto">
            <TopBar />
          </div>
          <div
            className="z-0 px-4 flex-1 absolute inset-0 no-scrollbar overflow-auto pb-64 pt-16"
          >
            <div className="space-y-4">
              <div className="flex-1 flex items-center justify-center">
                <div
                  style={{
                    maxWidth: EditorContainerWidth[viewport],
                    width: "100%",
                  }}
                  className={cn(
                    "m-4 transition-all max-w-full duration-300 ease-in-out pointer-events-auto",
                  )}
                >
                  {templateType === "email" ? (
                    <EmailEditor {...editorProps} />
                  ) : (
                    <WebEditor {...editorProps} />
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="z-[50] pointer-events-auto">
            {isWorkflowCustomMode ? (
              <WorkflowBottomBar
                onSave={onWorkflowSave}
                onCancel={onWorkflowCancel}
                workflowSubject={workflowSubject}
                hiddenBlocks={hiddenBlocks}
              />
            ) : (
              <BottomBar />
            )}
          </div>
        </div>
        <RightSidebar
          shop={shop}
          editorMode={editorMode}
          hiddenBlocks={hiddenBlocks}
          onToggleBlockVisibility={onToggleBlockVisibility}
          customizedBlockIds={customizedBlockIds}
        />
      </EditorSelectedBlockProvider>
    </div>
  );

  if (isWorkflowCustomMode) {
    return editorContent;
  }

  return <EditorDndContext>{editorContent}</EditorDndContext>;
}

function WorkflowBottomBar({
  onSave,
  onCancel,
  workflowSubject,
  hiddenBlocks,
}: {
  onSave?: () => void;
  onCancel?: () => void;
  workflowSubject?: Record<string, string>;
  hiddenBlocks?: string[];
}) {
  const { t } = useTranslation("trackoffice");
  const form = useFormContext<EmailTemplateFormData>();
  const { validateTemplate } = useTemplateValidation();
  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);
  const [validationErrors, setValidationErrors] = useState<Array<{
    type: "missing_translation" | "missing_subject_translation";
    severity: "error" | "warning";
    blockId?: string;
  }>>([]);

  const handleSave = () => {
    const data = form.getValues();
    const dataToValidate = workflowSubject
      ? { ...data, subject: workflowSubject }
      : data;
    // The workflow customization editor edits the workflow-level subject, so we
    // always validate it — even for v2 shops where the standalone template
    // editor skips the subject check. Blocks hidden in this workflow are
    // excluded: they won't be sent, so their missing translations don't matter.
    const errors = validateTemplate(dataToValidate, {
      validateSubject: true,
      hiddenBlockIds: hiddenBlocks,
    });

    if (errors.length) {
      setValidationErrors(errors);
      setIsValidationModalOpen(true);
      return;
    }

    onSave?.();
  };

  const handleSaveAnyway = () => {
    setIsValidationModalOpen(false);
    onSave?.();
  };

  return (
    <>
      <div className="w-full flex items-center justify-center">
        <FloatingBarGroup>
          <FloatingBar>
            <Button
              variant="ghost"
              size="sm"
              onClick={onCancel}
              className="rounded-full shadow-none"
            >
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              size="sm"
              className="rounded-full"
              onClick={handleSave}
            >
              <Save className="h-4 w-4 mr-1" />
              {t("workflowsEditor.emailCustomization.save", "Save customization")}
            </Button>
          </FloatingBar>
        </FloatingBarGroup>
      </div>

      <Dialog open={isValidationModalOpen} onOpenChange={setIsValidationModalOpen}>
        <DialogContent className="sm:max-w-[525px]">
          <DialogHeader>
            <DialogTitle>
              {t(
                "notificationsTemplatesEditor.validation.modal.title",
                "Template Validation Errors",
              )}
            </DialogTitle>
            <DialogDescription>
              {t(
                "notificationsTemplatesEditor.validation.modal.description",
                "Please fix the following errors before saving the template.",
              )}
            </DialogDescription>
          </DialogHeader>
          <ValidationErrorsDisplay validationItems={validationErrors} />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsValidationModalOpen(false)}>
              {t("common.close", "Close")}
            </Button>
            <Button onClick={handleSaveAnyway}>
              {t(
                "notificationsTemplatesEditor.validation.modal.saveAnyway",
                "Save Anyway",
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

function BlockUnselectHandler() {
  const { setSelectedBlock } = useEditorSelectedBlock();

  const handleClick = () => {
    setSelectedBlock(null);
  };

  return (
    <div
      onClick={handleClick}
      className="absolute w-full h-full pointer-events-all z-0"
    ></div>
  );
}
